import {
  safeGetLocalStorageItem,
  safeRemoveLocalStorageItem,
  safeSetLocalStorageItem,
  MOBILE_SCREEN_QUERY,
  formatCurrency,
} from "./shared.966473804198.js";

/**
 * Helper for calculating possibly-null inputs.
 */
const getFloat = (value) => {
  return value ? parseFloat(value) : 0;
};

/**
 * Inputs:
 * - totalPrice: used for calculating the result
 * - serviceCode, providerType, providerId: used for analytics and localStorage key
 *
 * Side effects:
 * - Reads to and writes from localStorage
 *
 * Emits:
 * - tqc:calculator-reset
 * - tqc:calculator-submit - with the formatted cost and form data attached to e.detail
 */
class CostShareCalculatorForm extends HTMLElement {
  constructor() {
    super();
    this.totalPrice = getFloat(this.getAttribute("data-total-price"));
    this.serviceCode = this.getAttribute("data-service-code");
    this.providerType = this.getAttribute("data-provider-id");
    this.providerId = this.getAttribute("data-provider-id");

    this.elements = {
      // Container
      form: this.querySelector("form"),

      // Inputs
      copay: this.querySelector("#copay"),
      coinsurance: this.querySelector("#coinsurance"),
      deductible: this.querySelector("#deductible"),
      oopMax: this.querySelector("#oop-max"),
      oopMaxReached: this.querySelector("#oop-max-radio-option-1"),
      oopMaxNotReached: this.querySelector("#oop-max-radio-option-2"),
      deductibleReached: this.querySelector("#deductible-radio-option-1"),
      deductibleNotReached: this.querySelector("#deductible-radio-option-2"),
      copayRadio: this.querySelector("#copay-coinsurance-option-1"),
      coinsuranceRadio: this.querySelector("#copay-coinsurance-option-2"),

      // Sections
      oopMaxSection: this.querySelector("#oop-max-section"),
      deductibleRadioSection: this.querySelector("#deductible-radio-section"),
      deductibleSection: this.querySelector("#deductible-section"),
      copayCoinsuranceSection: this.querySelector("#copay-coinsurance-section"),
      copaySection: this.querySelector("#copay-section"),
      coinsuranceSection: this.querySelector("#coinsurance-section"),

      // Buttons
      resetButton: this.querySelector("#reset-button"),
      calculateButton: this.querySelector("#calculate-button"),
    };
  }

  connectedCallback() {
    document.addEventListener("tqc:calculator-reset", this.handleReset);
    this.elements.resetButton.addEventListener("click", this.handleResetButton);
    this.elements.form.addEventListener("input", this.handleFormInput);
    this.elements.calculateButton.addEventListener(
      "click",
      this.handleSubmitCalculation,
    );
    this.disableNumberWheel();
    this.bindProgressiveFlowEvents();
    this.initializeCalculatorFromLocalStorage();
  }

  isCalculatorFormValid() {
    if (this.elements.oopMaxReached.checked) {
      return true;
    }
    if (this.elements.oopMax.value.length == 0) {
      return false;
    }
    if (
      this.elements.deductibleNotReached.checked &&
      this.elements.deductible.value.length == 0
    ) {
      return false;
    }

    if (this.elements.copayRadio.checked) {
      return this.elements.copay.value.length > 0;
    } else if (this.elements.coinsuranceRadio.checked) {
      return this.elements.coinsurance.value.length > 0;
    } else {
      return false;
    }
  }

  initializeCalculatorFromLocalStorage() {
    const elementNamesToStorageItems = this._retrieveLocalStorageItems();
    const valueElements =
      this._getElementsToStoreInLocalStorage().valueElements;
    const radioElements =
      this._getElementsToStoreInLocalStorage().radioElements;
    if (Object.keys(elementNamesToStorageItems).length == 0) {
      this.removeCopayAndCoinsurance();
      this.removeDeductible();
      this.removeOopMaxAmount();
    }
    let isCopayOrCoinsuranceChecked = false;
    for (let [elementName, storageValue] of Object.entries(
      elementNamesToStorageItems,
    )) {
      if (valueElements.includes(elementName)) {
        const htmlElement = document.getElementsByName(`${elementName}`)[0];
        htmlElement.value = storageValue;
      } else {
        const htmlElement = document.getElementById(`${storageValue}`);
        htmlElement.checked = true;
        if (
          elementName == "oop-max-radio" &&
          storageValue == "oop-max-radio-option-1"
        ) {
          this.elements.oopMaxReached.click();
          break;
        }
        if (
          elementName == "deductible-radio" &&
          storageValue == "deductible-radio-option-1"
        ) {
          this.elements.deductibleReached.click();
        }
        if (
          elementName == "copay-coinsurance" &&
          storageValue == "copay-coinsurance-option-1"
        ) {
          isCopayOrCoinsuranceChecked = true;
          this.elements.copayRadio.click();
        }
        if (
          elementName == "copay-coinsurance" &&
          storageValue == "copay-coinsurance-option-2"
        ) {
          isCopayOrCoinsuranceChecked = true;
          this.elements.coinsuranceRadio.click();
        }
      }
    }
    if (!isCopayOrCoinsuranceChecked) {
      this.removeCopayAmount();
      this.removeCoinsuranceAmount();
    }
    if (this.isCalculatorFormValid()) {
      this.elements.calculateButton.removeAttribute("disabled");
    }
  }

  disableNumberWheel() {
    this.elements.form.addEventListener("wheel", () => {
      if (document.activeElement.type === "number") {
        document.activeElement.blur();
      }
    });
  }

  showOopMaxAmount() {
    this.elements.oopMaxSection.classList.remove("pv-hide");
  }

  showDeductibleRadios() {
    this.elements.deductibleRadioSection.classList.remove("pv-hide");
  }

  showDeductibleAmount() {
    this.elements.deductibleSection.classList.remove("pv-hide");
  }

  showCopayCoinsuranceRadios() {
    this.elements.copayCoinsuranceSection.classList.remove("pv-hide");
  }

  showCopayAmount() {
    this.elements.copaySection.classList.remove("pv-hide");
  }

  showCoinsuranceAmount() {
    this.elements.coinsuranceSection.classList.remove("pv-hide");
  }

  removeOopMaxAmount() {
    this.elements.oopMaxSection.classList.add("pv-hide");
    this.elements.oopMax.value = "";
  }

  removeDeductibleAmount() {
    this.elements.deductibleSection.classList.add("pv-hide");
    this.elements.deductible.value = "";
  }

  removeDeductible() {
    this.elements.deductibleRadioSection.classList.add("pv-hide");
    this.elements.deductibleReached.checked = false;
    this.elements.deductibleNotReached.checked = false;
    this.removeDeductibleAmount();
  }

  removeCopayAmount() {
    this.elements.copaySection.classList.add("pv-hide");
    this.elements.copay.value = "";
  }

  removeCoinsuranceAmount() {
    this.elements.coinsuranceSection.classList.add("pv-hide");
    this.elements.coinsurance.value = "";
  }

  removeCopayAndCoinsurance() {
    this.elements.copayCoinsuranceSection.classList.add("pv-hide");
    this.elements.copayRadio.checked = false;
    this.elements.coinsuranceRadio.checked = false;
    this.removeCopayAmount();
    this.removeCoinsuranceAmount();
  }

  bindProgressiveFlowEvents() {
    this.elements.oopMaxReached.addEventListener("click", () => {
      this.removeCopayAndCoinsurance();
      this.removeDeductible();
      this.removeOopMaxAmount();
    });
    this.elements.oopMaxNotReached.addEventListener("click", () => {
      this.showOopMaxAmount();
      this.showDeductibleRadios();
    });
    this.elements.deductibleReached.addEventListener("click", () => {
      this.removeDeductibleAmount();
      this.showCopayCoinsuranceRadios();
    });
    this.elements.deductibleNotReached.addEventListener("click", () => {
      this.showDeductibleAmount();
      this.showCopayCoinsuranceRadios();
    });
    this.elements.copayRadio.addEventListener("click", () => {
      this.showCopayAmount();
      this.removeCoinsuranceAmount();
    });
    this.elements.coinsuranceRadio.addEventListener("click", () => {
      this.showCoinsuranceAmount();
      this.removeCopayAmount();
    });
  }

  handleResetButton = () => {
    document.dispatchEvent(new CustomEvent("tqc:calculator-reset"));
  };

  handleReset = () => {
    this.removeCopayAndCoinsurance();
    this.removeDeductible();
    this.removeOopMaxAmount();
    this.elements.oopMaxReached.checked = false;
    this.elements.oopMaxNotReached.checked = false;
    this.elements.calculateButton.setAttribute("disabled", "disabled");
    this.removeRelevantLocalStorageItems();
  };

  handleFormInput = () => {
    if (this.isCalculatorFormValid()) {
      this.elements.calculateButton.removeAttribute("disabled");
    } else {
      this.elements.calculateButton.setAttribute("disabled", "disabled");
    }
  };

  handleSubmitCalculation = () => {
    const form = this.elements.form;

    // Trigger native HTML5 form validation
    if (!form.checkValidity()) {
      form.reportValidity(); // Display native validation errors
      return;
    }

    const cost = this.calculatePatientCost();
    const roundedCost = Math.round(cost);
    const costAsCurrency = formatCurrency(roundedCost);

    const event = new CustomEvent("tqc:calculator-submit", {
      bubbles: true,
      detail: {
        currencyPatientCost: costAsCurrency,
        formData: new FormData(this.elements.form),
        roundedCost,
      },
    });
    document.dispatchEvent(event);

    this.saveDeductibleAndOOPToLocalStorage();
    this.saveCopayCoInsuranceToLocalStorage();
  };

  calculatePatientCost() {
    const deductibleLeft = getFloat(this.elements.deductible.value);
    const copayAmount = getFloat(this.elements.copay.value);
    const coinsurancePercent = getFloat(this.elements.coinsurance.value) / 100;
    const oopLeft = getFloat(this.elements.oopMax.value);

    if (this.elements.oopMaxReached.checked) {
      return 0;
    }
    let deductibleBasedCost;
    if (this.totalPrice <= deductibleLeft) {
      deductibleBasedCost = this.totalPrice;
    } else {
      // Some plans may differ,
      // but in the generic case, copay is only applied
      // after you meet the deductible
      deductibleBasedCost =
        deductibleLeft +
        (this.totalPrice - deductibleLeft) * coinsurancePercent +
        copayAmount;
    }
    const cost = Math.min(deductibleBasedCost, oopLeft);
    return cost;
  }

  _createLocalStorageKey(elementToStore, useServiceAndProvider = false) {
    const baseKeys = [this.selectedPlanName, elementToStore];
    let additionalKeys = [];
    if (useServiceAndProvider) {
      const packageId = this.serviceCode;
      const providerType = this.providerType;
      const providerId = this.providerId;
      additionalKeys = [providerId, providerType, packageId];
    }
    return additionalKeys.concat(baseKeys).join("_");
  }

  _retrieveLocalStorageItems() {
    const keys = {};
    const elementNameToStorageItemMap = {};
    const storageElementsByInsurance =
      this._getElementsToStoreInLocalStorage().byInsurance;
    const storageElementsByInsuranceProviderService =
      this._getElementsToStoreInLocalStorage().byInsuranceProviderService;
    for (let item of storageElementsByInsurance.concat(
      storageElementsByInsuranceProviderService,
    )) {
      if (storageElementsByInsurance.includes(item)) {
        keys[item] = this._createLocalStorageKey(item);
      } else {
        keys[item] = this._createLocalStorageKey(item, true);
      }
    }
    for (let [elementName, storageKey] of Object.entries(keys)) {
      const storageItem = safeGetLocalStorageItem(storageKey);
      if (storageItem) {
        elementNameToStorageItemMap[elementName] = storageItem;
      }
    }
    return elementNameToStorageItemMap;
  }
  _getElementsToStoreInLocalStorage() {
    const deductibleAndOOPValues = ["oop-max", "deductible"];
    const deductibleAndOOPRadios = ["oop-max-radio", "deductible-radio"];
    const copayOrCoinsuranceRadio = ["copay-coinsurance"];
    const copayAndCoinsuranceValues = ["copay", "coinsurance"];
    return {
      deductibleAndOOPValues: deductibleAndOOPValues,
      deductibleAndOOPRadios: deductibleAndOOPRadios,
      copayOrCoinsuranceRadio: copayOrCoinsuranceRadio,
      copayAndCoinsuranceValues: copayAndCoinsuranceValues,
      byInsurance: deductibleAndOOPRadios.concat(deductibleAndOOPValues),
      byInsuranceProviderService: copayOrCoinsuranceRadio.concat(
        copayAndCoinsuranceValues,
      ),
      valueElements: deductibleAndOOPValues.concat(copayAndCoinsuranceValues),
      radioElements: deductibleAndOOPRadios.concat(copayOrCoinsuranceRadio),
    };
  }
  removeRelevantLocalStorageItems() {
    const keysToRemove = [];
    const storageElementsByInsurance =
      this._getElementsToStoreInLocalStorage().byInsurance;
    const storageElementsByInsuranceProviderService =
      this._getElementsToStoreInLocalStorage().byInsuranceProviderService;
    for (let item of storageElementsByInsurance.concat(
      storageElementsByInsuranceProviderService,
    )) {
      if (storageElementsByInsurance.includes(item)) {
        keysToRemove.push(this._createLocalStorageKey(item));
      } else {
        keysToRemove.push(this._createLocalStorageKey(item, true));
      }
    }
    for (let key of keysToRemove) {
      safeRemoveLocalStorageItem(key);
    }
  }
  saveDeductibleAndOOPToLocalStorage() {
    const valueElementNames =
      this._getElementsToStoreInLocalStorage().deductibleAndOOPValues;
    const radioElementNames =
      this._getElementsToStoreInLocalStorage().deductibleAndOOPRadios;
    const elements = valueElementNames.concat(radioElementNames);
    for (let element of elements) {
      const key = this._createLocalStorageKey(element);
      safeRemoveLocalStorageItem(key);
      let value = "";
      if (valueElementNames.includes(element)) {
        value = document.querySelector(`input[name=${element}]`).value;
      }
      if (radioElementNames.includes(element)) {
        let input = document.querySelector(`input[name=${element}]:checked`);
        if (input) {
          value = input.id;
        }
      }
      if (value) {
        safeSetLocalStorageItem(key, value);
      }
    }
  }
  saveCopayCoInsuranceToLocalStorage() {
    const [copayOrCoinsuranceRadioName] =
      this._getElementsToStoreInLocalStorage().copayOrCoinsuranceRadio;
    const [copayInputName, coinsuranceInputName] =
      this._getElementsToStoreInLocalStorage().copayAndCoinsuranceValues;
    const isCoinsuranceChecked = this.elements.coinsuranceRadio.checked;
    const isCopayChecked = this.elements.copayRadio.checked;
    const copayOrCoinsuranceKey = this._createLocalStorageKey(
      copayOrCoinsuranceRadioName,
      true,
    );
    const coinsuranceInputKey = this._createLocalStorageKey(
      coinsuranceInputName,
      true,
    );
    const copayInputKey = this._createLocalStorageKey(copayInputName, true);

    // Remove all related localstorage items
    safeRemoveLocalStorageItem(copayOrCoinsuranceKey);
    safeRemoveLocalStorageItem(coinsuranceInputKey);
    safeRemoveLocalStorageItem(copayInputKey);

    // Set appropriate localstorage items
    if (isCoinsuranceChecked) {
      safeSetLocalStorageItem(
        copayOrCoinsuranceKey,
        this.elements.coinsuranceRadio.id,
      );
      safeSetLocalStorageItem(
        coinsuranceInputKey,
        this.elements.coinsurance.value,
      );
    }
    if (isCopayChecked) {
      safeSetLocalStorageItem(
        copayOrCoinsuranceKey,
        this.elements.copayRadio.id,
      );
      safeSetLocalStorageItem(copayInputKey, this.elements.copay.value);
    }
  }
}

customElements.define("cost-share-calculator-form", CostShareCalculatorForm);
